import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "../common/Button";
import { bugsnagClient } from "../../bugsnag";
import { BACKEND_URL } from "../../config/config";
import { ANALYTICS_EVENTS, trackEvent } from "../../utils/analytics";
import api from "../../utils/api";

const SUPPORT_EMAIL = "contact@monco.ai";

const INVALID_INVITATION_MESSAGE =
  "This invitation link is invalid or has expired. To get a valid invitation link, please contact the Monco team at";

const NETWORK_INVITATION_MESSAGE =
  "We couldn't validate your invitation link. Please check your connection and try again, or contact the Monco team at";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    invitationToken: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [inviteStatus, setInviteStatus] = useState(
    /** @type {"checking" | "valid" | "invalid"} */ ("checking"),
  );

  // Read token from /invite/:token or ?token= query; validate and prefill email
  useEffect(() => {
    const queryToken = new URLSearchParams(location.search).get("token");
    const routeToken = params.token;
    const token = routeToken || queryToken;

    if (!token) {
      setInviteStatus("invalid");
      setInviteError(INVALID_INVITATION_MESSAGE);
      return;
    }

    const run = async () => {
      try {
        const data = await api.validateInvite(token);
        if (data && !data.message) {
          if (data.isEmailRegistered) {
            navigate("/login", { replace: true });
            return;
          }
          setInviteInfo(data);
          setInviteStatus("valid");
          setFormData((prev) => ({
            ...prev,
            email: data.email || "",
            invitationToken: token,
          }));
        } else {
          setInviteStatus("invalid");
          setInviteError(INVALID_INVITATION_MESSAGE);
        }
      } catch (e) {
        setInviteStatus("invalid");
        setInviteError(NETWORK_INVITATION_MESSAGE);
        bugsnagClient.notify(e);
      }
    };
    run();
  }, [location.search, navigate, params.token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const { confirmPassword, ...payload } = formData;
      if (!formData.password || formData.password.length < 6) {
        throw new Error("Password must be at least 6 characters");
      }
      if (formData.password !== confirmPassword) {
        throw new Error("Passwords do not match");
      }

      const response = await fetch(`${BACKEND_URL}/api/users/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      // Show success message and prepare welcome banner
      const orgName = data.organization?.name || inviteInfo?.organization?.name;
      setSuccessMessage(
        orgName
          ? `Welcome to ${orgName}!`
          : data.message || "Registration successful.",
      );

      trackEvent(ANALYTICS_EVENTS.USER_REGISTERED, {
        hasInvitation: !!formData.invitationToken,
        organizationName: orgName || null,
      });

      // Store tokens and banner
      localStorage.setItem("token", data.token);
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }
      if (orgName) {
        sessionStorage.setItem("org_welcome", `Welcome to ${orgName}!`);
      }

      // Navigate after a short delay to show the message
      setTimeout(() => {
        if (data.organization?._id) {
          window.location.href = "/organization";
        } else {
          window.location.href = "/";
        }
      }, 1500);
    } catch (err) {
      setError(err.message);
      bugsnagClient.notify(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gray-50 py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          src="/monco-logo-black.png"
          alt="Monco.ai"
          className="mx-auto h-12 w-auto"
        />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {inviteStatus === "valid"
            ? `Join ${inviteInfo?.orgName}`
            : inviteStatus === "invalid"
              ? "Invalid or expired invitation"
              : "Checking your invitation..."}
        </h2>
      </div>
      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
        <div
          className={`px-4 sm:px-10 ${
            inviteStatus === "invalid"
              ? ""
              : "py-8 bg-white shadow sm:rounded-lg"
          }`}
        >
          {inviteStatus === "checking" && (
            <div className="flex flex-col items-center py-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
              <p className="mt-4 text-sm text-gray-500">
                Validating your invitation link...
              </p>
            </div>
          )}
          {inviteStatus === "invalid" && (
            <>
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm text-center">
                {inviteError || INVALID_INVITATION_MESSAGE}{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="font-medium underline hover:text-red-800"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
              <p className="mt-6 text-center text-sm text-gray-600">
                Already have an account?
              </p>
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate("/login")}
                className="mt-3 w-full"
              >
                Log in
              </Button>
            </>
          )}
          {inviteStatus === "valid" && (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
                  {successMessage}
                </div>
              )}
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  First name
                </label>
                <div className="mt-1">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Last name
                </label>
                <div className="mt-1">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.email}
                    onChange={handleChange}
                    readOnly={!!inviteInfo?.email}
                    disabled={!!inviteInfo?.email}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isLoading || successMessage}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
                >
                  {isLoading
                    ? "Creating account..."
                    : successMessage
                      ? "Redirecting..."
                      : "Create account"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
